import React, { useEffect, useMemo, useState, useRef } from 'react';
import { adminListProducts, adminCreateProduct, adminUpdateProduct, adminDeleteProduct, adminListCategories, productTemplateUrl, adminBulkUploadProducts, adminImportFromSheet, adminUploadImageFile } from '../../lib/api';
import { toast } from 'sonner';
import { Plus, Edit3, Trash2, X, Search, Layers, Image as ImageIcon, Sparkles, Info, Tag as TagIcon, Upload, Download, FileSpreadsheet, Loader2 } from 'lucide-react';
import { useCurrency } from '../../context/CurrencyContext';

const empty = {
  name: '', slug: '', category: '', collections: [], price: 0, original_price: 0,
  image: '', hover_image: '', gallery: [], videos: [], verse: '',
  tag: '', ribbon: '', colors: [], description: '', stock: 100, sku: '',
  active: true, product_type: 'physical', digital_file_url: '',
  weight_grams: 500,
  personalization_enabled: false, personalization_label: 'Personalize (optional)', personalization_max_length: 40,
  variant_options: [], variants: [],
  seo_title: '', seo_description: '', seo_keywords: '',
};

const cartesian = (arrs) => arrs.reduce((acc, arr) => acc.flatMap(a => arr.map(v => [...a, v])), [[]]);

const AdminProducts = () => {
  const { format } = useCurrency();
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState('basic');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [sheetUrl, setSheetUrl] = useState('');
  const fileRef = useRef(null);
  const singleImgRef = useRef(null);
  const hoverImgRef = useRef(null);
  const galleryImgRef = useRef(null);
  const [imgUploading, setImgUploading] = useState('');   // '', 'primary', 'hover', 'gallery'

  const uploadImageTo = async (file, target, galleryIdx) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) { toast.error('Please choose an image file'); return; }
    setImgUploading(target);
    try {
      const meta = await adminUploadImageFile(file);
      const url = meta.url?.startsWith('http') ? meta.url : (process.env.REACT_APP_BACKEND_URL + meta.url);
      setEditing(prev => {
        if (target === 'primary') return { ...prev, image: url };
        if (target === 'hover')   return { ...prev, hover_image: url };
        if (target === 'gallery') {
          const arr = [...(prev.gallery || [])];
          if (typeof galleryIdx === 'number') arr[galleryIdx] = url; else arr.push(url);
          return { ...prev, gallery: arr };
        }
        return prev;
      });
      toast.success('Image uploaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setImgUploading('');
      if (singleImgRef.current) singleImgRef.current.value = '';
      if (hoverImgRef.current) hoverImgRef.current.value = '';
      if (galleryImgRef.current) galleryImgRef.current.value = '';
    }
  };

  const load = () => adminListProducts().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); adminListCategories().then(setCats); }, []);

  const doBulk = async (file) => {
    if (!file) return;
    setUploading(true); setBulkResult(null);
    try {
      const res = await adminBulkUploadProducts(file);
      setBulkResult(res);
      toast.success(`Bulk upload: ${res.created} created · ${res.updated} updated · ${res.errors?.length || 0} errors`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const doSheetImport = async () => {
    if (!sheetUrl.trim()) return;
    setUploading(true); setBulkResult(null);
    try {
      const res = await adminImportFromSheet(sheetUrl.trim());
      setBulkResult(res);
      toast.success(`Sheet import: ${res.created} created · ${res.updated} updated · ${res.errors?.length || 0} errors`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sheet import failed');
    } finally {
      setUploading(false);
    }
  };

  const filtered = useMemo(() => items.filter(p => !q || p.name.toLowerCase().includes(q.toLowerCase()) || (p.sku || '').toLowerCase().includes(q.toLowerCase())), [items, q]);

  const startEdit = (p) => {
    setTab('basic');
    setEditing(p ? {
      ...empty, ...p,
      gallery: p.gallery || [], videos: p.videos || [], variants: p.variants || [], variant_options: p.variant_options || [],
      colors: (p.colors || []).join(', '),
    } : { ...empty });
  };

  const onSave = async () => {
    const payload = {
      ...editing,
      price: +editing.price, original_price: +editing.original_price, stock: +editing.stock,
      weight_grams: +editing.weight_grams,
      colors: typeof editing.colors === 'string' ? editing.colors.split(',').map(s => s.trim()).filter(Boolean) : (editing.colors || []),
      personalization_max_length: +editing.personalization_max_length,
    };
    try {
      if (editing.id) await adminUpdateProduct(editing.id, payload); else await adminCreateProduct(payload);
      toast.success(editing.id ? 'Product updated' : 'Product created');
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  const onDelete = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    await adminDeleteProduct(p.id); toast.success('Deleted'); load();
  };

  // Variant helpers
  const addOption = () => setEditing(e => ({ ...e, variant_options: [...(e.variant_options || []), { name: '', values: [] }] }));
  const updateOption = (idx, patch) => setEditing(e => { const o = [...e.variant_options]; o[idx] = { ...o[idx], ...patch }; return { ...e, variant_options: o }; });
  const removeOption = (idx) => setEditing(e => { const o = e.variant_options.filter((_, i) => i !== idx); return { ...e, variant_options: o }; });

  const generateVariants = () => {
    const opts = (editing.variant_options || []).filter(o => o.name && o.values?.length);
    if (opts.length === 0) { toast.error('Add at least one option with values'); return; }
    const combos = cartesian(opts.map(o => o.values.map(v => [o.name, v])));
    const skuBase = editing.sku || `WTH-${(editing.name || 'PROD').toUpperCase().slice(0, 8).replace(/\s+/g, '')}`;
    const variants = combos.map((combo, idx) => {
      const attrs = Object.fromEntries(combo);
      const suffix = combo.map(([, v]) => v.slice(0, 3).toUpperCase()).join('-');
      const existing = (editing.variants || []).find(v => Object.keys(attrs).every(k => v.attrs?.[k] === attrs[k]));
      return existing || {
        id: `v_${Date.now()}_${idx}`,
        attrs, price: +editing.price, original_price: +editing.original_price,
        stock: 10, sku: `${skuBase}-${suffix}`, image: editing.image,
      };
    });
    setEditing(e => ({ ...e, variants }));
    toast.success(`Generated ${variants.length} variants`);
  };

  const updateVariant = (idx, patch) => setEditing(e => { const v = [...e.variants]; v[idx] = { ...v[idx], ...patch }; return { ...e, variants: v }; });

  const setListField = (field, arr) => setEditing(e => ({ ...e, [field]: arr }));

  const TABS = [
    { id: 'basic', label: 'Basic', icon: Info },
    { id: 'media', label: 'Media', icon: ImageIcon },
    { id: 'pricing', label: 'Pricing & SKU', icon: TagIcon },
    { id: 'variants', label: 'Variants', icon: Layers },
    { id: 'personalization', label: 'Personalization', icon: Sparkles },
    { id: 'seo', label: 'SEO', icon: Search },
  ];

  return (
    <div>
      <div className="flex items-end justify-between mb-8 gap-4">
        <div>
          <h1 className="font-serif-display text-4xl">Products</h1>
          <p className="text-sm text-neutral-600 mt-1">{items.length} total</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Name or SKU" className="pl-10 pr-4 py-2.5 bg-white border border-[#E7DCC7] rounded-full text-sm outline-none focus:border-[#D4A63A] w-64" />
          </div>
          <button
            onClick={() => { setBulkOpen(true); setBulkResult(null); }}
            data-testid="bulk-upload-btn"
            className="inline-flex items-center gap-2 bg-white border border-[#E7DCC7] hover:border-[#D4A63A] text-[#213A60] px-5 py-2.5 rounded-full text-xs uppercase tracking-widest transition-colors"
          >
            <FileSpreadsheet className="w-4 h-4" /> Bulk Upload
          </button>
          <button onClick={() => startEdit(null)} className="inline-flex items-center gap-2 bg-[#213A60] hover:bg-[#D4A63A] text-white px-5 py-2.5 rounded-full text-xs uppercase tracking-widest transition-colors"><Plus className="w-4 h-4" /> New</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {filtered.map(p => (
          <div key={p.id} className="bg-white rounded-xl border border-[#E7DCC7] overflow-hidden">
            <div className="aspect-square overflow-hidden bg-neutral-100 relative">
              <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
              {p.ribbon && <span className="absolute top-2 left-2 shimmer-badge text-white text-[9px] uppercase tracking-widest px-2 py-0.5 rounded">{p.ribbon}</span>}
              {p.variants?.length > 0 && <span className="absolute bottom-2 left-2 bg-white/90 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded">{p.variants.length} variants</span>}
            </div>
            <div className="p-4">
              <div className="text-sm font-medium line-clamp-1">{p.name}</div>
              <div className="text-[10px] font-mono text-neutral-500 mt-0.5">{p.sku || '—'}</div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-[#8A6A22] font-semibold text-sm">{format(p.price)}</span>
                <span className={`text-[10px] uppercase px-2 py-0.5 rounded ${p.active ? 'bg-green-50 text-green-700' : 'bg-neutral-100 text-neutral-500'}`}>{p.active ? 'Active' : 'Hidden'}</span>
              </div>
              <div className="text-[11px] text-neutral-500 mt-1">Stock: {p.stock ?? 0}</div>
              <div className="flex items-center gap-2 mt-3">
                <button onClick={() => startEdit(p)} className="flex-1 inline-flex items-center justify-center gap-1 text-xs border border-[#E7DCC7] rounded-full py-2 hover:border-[#D4A63A] hover:text-[#8A6A22]"><Edit3 className="w-3 h-3" /> Edit</button>
                <button onClick={() => onDelete(p)} className="px-3 py-2 text-xs border border-[#E7DCC7] rounded-full hover:border-red-500 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <>
          <div onClick={() => setEditing(null)} className="fixed inset-0 bg-black/50 z-50" />
          <aside className="fixed top-0 right-0 h-full w-full sm:w-[720px] bg-white z-50 overflow-y-auto shadow-2xl flex flex-col">
            <div className="px-6 py-5 border-b border-[#E7DCC7] flex items-center justify-between shrink-0">
              <div>
                <h3 className="font-serif-display text-2xl">{editing.id ? 'Edit Product' : 'New Product'}</h3>
                {editing.sku && <div className="text-[11px] font-mono text-neutral-500 mt-0.5">{editing.sku}</div>}
              </div>
              <button onClick={() => setEditing(null)}><X className="w-5 h-5" /></button>
            </div>

            <div className="border-b border-[#E7DCC7] px-6 shrink-0 overflow-x-auto">
              <div className="flex gap-1">
                {TABS.map(t => {
                  const I = t.icon;
                  return (
                    <button key={t.id} onClick={() => setTab(t.id)} className={`inline-flex items-center gap-1.5 px-4 py-3 text-xs uppercase tracking-widest border-b-2 whitespace-nowrap transition-colors ${tab === t.id ? 'border-[#D4A63A] text-[#8A6A22]' : 'border-transparent text-neutral-500 hover:text-[#213A60]'}`}>
                      <I className="w-3.5 h-3.5" /> {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
              {tab === 'basic' && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block col-span-2"><span className="text-[11px] uppercase tracking-widest text-neutral-600">Name</span><input value={editing.name || ''} onChange={e => setEditing({ ...editing, name: e.target.value })} className="mt-1 w-full bg-[#F8F4EC] border border-[#E7DCC7] rounded-full px-4 py-2.5 text-sm outline-none focus:border-[#D4A63A]" /></label>
                    <label className="block"><span className="text-[11px] uppercase tracking-widest text-neutral-600">Slug</span><input value={editing.slug || ''} onChange={e => setEditing({ ...editing, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })} className="mt-1 w-full bg-[#F8F4EC] border border-[#E7DCC7] rounded-full px-4 py-2.5 text-sm outline-none focus:border-[#D4A63A] font-mono" /></label>
                    <label className="block"><span className="text-[11px] uppercase tracking-widest text-neutral-600">Category</span><select value={editing.category || ''} onChange={e => setEditing({ ...editing, category: e.target.value })} className="mt-1 w-full bg-[#F8F4EC] border border-[#E7DCC7] rounded-full px-4 py-2.5 text-sm outline-none focus:border-[#D4A63A]"><option value="">Select</option>{cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></label>
                  </div>
                  <label className="block"><span className="text-[11px] uppercase tracking-widest text-neutral-600">Verse / Subtitle</span><input value={editing.verse || ''} onChange={e => setEditing({ ...editing, verse: e.target.value })} className="mt-1 w-full bg-[#F8F4EC] border border-[#E7DCC7] rounded-full px-4 py-2.5 text-sm outline-none focus:border-[#D4A63A]" /></label>
                  <label className="block"><span className="text-[11px] uppercase tracking-widest text-neutral-600">Description</span><textarea rows="4" value={editing.description || ''} onChange={e => setEditing({ ...editing, description: e.target.value })} className="mt-1 w-full bg-[#F8F4EC] border border-[#E7DCC7] rounded-lg px-4 py-2.5 text-sm outline-none focus:border-[#D4A63A]" /></label>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block"><span className="text-[11px] uppercase tracking-widest text-neutral-600">Ribbon</span><select value={editing.ribbon || ''} onChange={e => setEditing({ ...editing, ribbon: e.target.value })} className="mt-1 w-full bg-[#F8F4EC] border border-[#E7DCC7] rounded-full px-4 py-2.5 text-sm outline-none focus:border-[#D4A63A]"><option value="">None</option><option value="New">New</option><option value="Sale">Sale</option><option value="Best Seller">Best Seller</option><option value="Trending">Trending</option><option value="Signature">Signature</option></select></label>
                    <label className="block"><span className="text-[11px] uppercase tracking-widest text-neutral-600">Product Type</span><select value={editing.product_type || 'physical'} onChange={e => setEditing({ ...editing, product_type: e.target.value })} className="mt-1 w-full bg-[#F8F4EC] border border-[#E7DCC7] rounded-full px-4 py-2.5 text-sm outline-none focus:border-[#D4A63A]"><option value="physical">Physical</option><option value="digital">Digital</option></select></label>
                  </div>
                  {editing.product_type === 'digital' && (
                    <label className="block"><span className="text-[11px] uppercase tracking-widest text-neutral-600">Digital File URL (delivered on purchase)</span><input value={editing.digital_file_url || ''} onChange={e => setEditing({ ...editing, digital_file_url: e.target.value })} className="mt-1 w-full bg-[#F8F4EC] border border-[#E7DCC7] rounded-full px-4 py-2.5 text-sm outline-none focus:border-[#D4A63A] font-mono" /></label>
                  )}
                  <label className="inline-flex items-center gap-2"><input type="checkbox" checked={!!editing.active} onChange={e => setEditing({ ...editing, active: e.target.checked })} /> <span className="text-sm">Active (visible on storefront)</span></label>
                </>
              )}

              {tab === 'media' && (
                <>
                  <div>
                    <span className="text-[11px] uppercase tracking-widest text-neutral-600">Primary Image</span>
                    <div className="mt-1 space-y-2">
                      {editing.image && (
                        <div className="relative">
                          <img src={editing.image} alt="" className="h-32 w-full object-cover rounded-lg border border-[#E7DCC7]" />
                          <button type="button" onClick={() => setEditing({ ...editing, image: '' })} className="absolute top-1 right-1 w-7 h-7 bg-white/95 rounded-full flex items-center justify-center hover:bg-white shadow"><X className="w-3.5 h-3.5" /></button>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <input value={editing.image || ''} onChange={e => setEditing({ ...editing, image: e.target.value })} placeholder="Paste image URL or use Upload" className="flex-1 bg-[#F8F4EC] border border-[#E7DCC7] rounded-full px-4 py-2 text-sm outline-none focus:border-[#D4A63A]" />
                        <input ref={singleImgRef} type="file" accept="image/*" className="hidden" onChange={e => uploadImageTo(e.target.files?.[0], 'primary')} />
                        <button type="button" onClick={() => singleImgRef.current?.click()} disabled={imgUploading === 'primary'} className="inline-flex items-center gap-2 border border-[#E7DCC7] hover:border-[#D4A63A] hover:text-[#8A6A22] rounded-full px-4 py-2 text-xs uppercase tracking-widest disabled:opacity-60">
                          {imgUploading === 'primary' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                          {imgUploading === 'primary' ? 'Uploading' : 'Upload'}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div>
                    <span className="text-[11px] uppercase tracking-widest text-neutral-600">Hover Image (shown on hover in Shop grid)</span>
                    <div className="mt-1 space-y-2">
                      {editing.hover_image && (
                        <div className="relative">
                          <img src={editing.hover_image} alt="" className="h-24 w-auto object-cover rounded-lg border border-[#E7DCC7]" />
                          <button type="button" onClick={() => setEditing({ ...editing, hover_image: '' })} className="absolute top-1 right-1 w-7 h-7 bg-white/95 rounded-full flex items-center justify-center hover:bg-white shadow"><X className="w-3.5 h-3.5" /></button>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <input value={editing.hover_image || ''} onChange={e => setEditing({ ...editing, hover_image: e.target.value })} placeholder="Paste image URL or use Upload" className="flex-1 bg-[#F8F4EC] border border-[#E7DCC7] rounded-full px-4 py-2 text-sm outline-none focus:border-[#D4A63A]" />
                        <input ref={hoverImgRef} type="file" accept="image/*" className="hidden" onChange={e => uploadImageTo(e.target.files?.[0], 'hover')} />
                        <button type="button" onClick={() => hoverImgRef.current?.click()} disabled={imgUploading === 'hover'} className="inline-flex items-center gap-2 border border-[#E7DCC7] hover:border-[#D4A63A] hover:text-[#8A6A22] rounded-full px-4 py-2 text-xs uppercase tracking-widest disabled:opacity-60">
                          {imgUploading === 'hover' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                          {imgUploading === 'hover' ? 'Uploading' : 'Upload'}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[11px] uppercase tracking-widest text-neutral-600">Gallery Images</span>
                      <div className="flex items-center gap-2">
                        <input ref={galleryImgRef} type="file" accept="image/*" className="hidden" onChange={e => uploadImageTo(e.target.files?.[0], 'gallery')} />
                        <button type="button" onClick={() => galleryImgRef.current?.click()} disabled={imgUploading === 'gallery'} className="text-xs text-[#8A6A22] inline-flex items-center gap-1 hover:text-[#D4A63A] disabled:opacity-60">
                          {imgUploading === 'gallery' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                          {imgUploading === 'gallery' ? 'Uploading' : 'Upload'}
                        </button>
                        <button type="button" onClick={() => setListField('gallery', [...(editing.gallery || []), ''])} className="text-xs text-[#8A6A22] inline-flex items-center gap-1 hover:text-[#D4A63A]"><Plus className="w-3 h-3" /> Add URL</button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {(editing.gallery || []).map((g, i) => (
                        <div key={i} className="flex gap-2 items-center">
                          {g && <img src={g} alt="" className="w-10 h-10 rounded object-cover border border-[#E7DCC7]" />}
                          <input value={g} onChange={e => { const arr = [...editing.gallery]; arr[i] = e.target.value; setListField('gallery', arr); }} placeholder="Image URL" className="flex-1 bg-[#F8F4EC] border border-[#E7DCC7] rounded-full px-4 py-2 text-sm outline-none focus:border-[#D4A63A]" />
                          <button type="button" onClick={() => setListField('gallery', editing.gallery.filter((_, k) => k !== i))} className="px-3 border border-[#E7DCC7] rounded-full text-neutral-500 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[11px] uppercase tracking-widest text-neutral-600">Videos (YouTube URLs)</span>
                      <button onClick={() => setListField('videos', [...(editing.videos || []), ''])} className="text-xs text-[#8A6A22] inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add</button>
                    </div>
                    <div className="space-y-2">
                      {(editing.videos || []).map((v, i) => (
                        <div key={i} className="flex gap-2">
                          <input value={v} onChange={e => { const arr = [...editing.videos]; arr[i] = e.target.value; setListField('videos', arr); }} placeholder="https://youtube.com/watch?v=..." className="flex-1 bg-[#F8F4EC] border border-[#E7DCC7] rounded-full px-4 py-2 text-sm outline-none focus:border-[#D4A63A]" />
                          <button onClick={() => setListField('videos', editing.videos.filter((_, k) => k !== i))} className="px-3 border border-[#E7DCC7] rounded-full text-neutral-500 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {tab === 'pricing' && (
                <>
                  <div className="grid grid-cols-3 gap-3">
                    <label className="block"><span className="text-[11px] uppercase tracking-widest text-neutral-600">Price ($)</span><input type="number" value={editing.price} onChange={e => setEditing({ ...editing, price: e.target.value })} className="mt-1 w-full bg-[#F8F4EC] border border-[#E7DCC7] rounded-full px-4 py-2.5 text-sm outline-none focus:border-[#D4A63A]" /></label>
                    <label className="block"><span className="text-[11px] uppercase tracking-widest text-neutral-600">MRP ($)</span><input type="number" value={editing.original_price} onChange={e => setEditing({ ...editing, original_price: e.target.value })} className="mt-1 w-full bg-[#F8F4EC] border border-[#E7DCC7] rounded-full px-4 py-2.5 text-sm outline-none focus:border-[#D4A63A]" /></label>
                    <label className="block"><span className="text-[11px] uppercase tracking-widest text-neutral-600">Stock</span><input type="number" value={editing.stock} onChange={e => setEditing({ ...editing, stock: e.target.value })} className="mt-1 w-full bg-[#F8F4EC] border border-[#E7DCC7] rounded-full px-4 py-2.5 text-sm outline-none focus:border-[#D4A63A]" /></label>
                  </div>
                  <label className="block"><span className="text-[11px] uppercase tracking-widest text-neutral-600">SKU</span><input value={editing.sku || ''} onChange={e => setEditing({ ...editing, sku: e.target.value.toUpperCase() })} placeholder="WTH-XXXXX" className="mt-1 w-full bg-[#F8F4EC] border border-[#E7DCC7] rounded-full px-4 py-2.5 text-sm outline-none focus:border-[#D4A63A] font-mono" /></label>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block"><span className="text-[11px] uppercase tracking-widest text-neutral-600">Weight (grams)</span><input type="number" value={editing.weight_grams || 500} onChange={e => setEditing({ ...editing, weight_grams: e.target.value })} className="mt-1 w-full bg-[#F8F4EC] border border-[#E7DCC7] rounded-full px-4 py-2.5 text-sm outline-none focus:border-[#D4A63A]" /></label>
                    <label className="block"><span className="text-[11px] uppercase tracking-widest text-neutral-600">Colors (comma sep)</span><input value={editing.colors || ''} onChange={e => setEditing({ ...editing, colors: e.target.value })} placeholder="#000, #fff" className="mt-1 w-full bg-[#F8F4EC] border border-[#E7DCC7] rounded-full px-4 py-2.5 text-sm outline-none focus:border-[#D4A63A]" /></label>
                  </div>
                </>
              )}

              {tab === 'variants' && (
                <>
                  <div className="bg-[#F8F4EC] rounded-xl p-4 text-xs text-neutral-600">
                    Define variant options (e.g. Size, Color). Then click <b>Generate Combinations</b> to auto-create all variants with individual price/stock/SKU.
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[11px] uppercase tracking-widest text-neutral-600">Option Types</span>
                      <button onClick={addOption} className="text-xs text-[#8A6A22] inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add Option</button>
                    </div>
                    <div className="space-y-3">
                      {(editing.variant_options || []).map((opt, i) => (
                        <div key={i} className="border border-[#E7DCC7] rounded-xl p-3 space-y-2">
                          <div className="flex gap-2">
                            <input value={opt.name} onChange={e => updateOption(i, { name: e.target.value })} placeholder="Option name (Size, Color…)" className="flex-1 bg-[#F8F4EC] border border-[#E7DCC7] rounded-full px-4 py-2 text-sm outline-none focus:border-[#D4A63A]" />
                            <button onClick={() => removeOption(i)} className="px-3 border border-[#E7DCC7] rounded-full text-neutral-500 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                          <input value={(opt.values || []).join(', ')} onChange={e => updateOption(i, { values: e.target.value.split(',').map(v => v.trim()).filter(Boolean) })} placeholder="Values (comma separated) e.g. S, M, L" className="w-full bg-white border border-[#E7DCC7] rounded-full px-4 py-2 text-sm outline-none focus:border-[#D4A63A]" />
                        </div>
                      ))}
                    </div>
                  </div>
                  <button onClick={generateVariants} className="w-full border border-dashed border-[#D4A63A] text-[#8A6A22] rounded-full py-3 text-xs uppercase tracking-widest hover:bg-[#D4A63A] hover:text-white transition-colors">Generate Combinations</button>

                  {(editing.variants || []).length > 0 && (
                    <div>
                      <div className="text-[11px] uppercase tracking-widest text-neutral-600 mb-2">Variants ({editing.variants.length})</div>
                      <div className="border border-[#E7DCC7] rounded-xl overflow-hidden">
                        <table className="w-full text-xs">
                          <thead className="bg-[#F8F4EC] text-neutral-600">
                            <tr><th className="text-left px-3 py-2">Options</th><th className="text-left px-3 py-2">Price</th><th className="text-left px-3 py-2">Stock</th><th className="text-left px-3 py-2">SKU</th></tr>
                          </thead>
                          <tbody className="divide-y divide-[#E7DCC7]">
                            {editing.variants.map((v, i) => (
                              <tr key={v.id || i}>
                                <td className="px-3 py-2">{Object.entries(v.attrs || {}).map(([k, val]) => `${k}: ${val}`).join(', ')}</td>
                                <td className="px-3 py-2"><input type="number" value={v.price} onChange={e => updateVariant(i, { price: +e.target.value })} className="w-20 bg-transparent border border-[#E7DCC7] rounded px-2 py-1" /></td>
                                <td className="px-3 py-2"><input type="number" value={v.stock} onChange={e => updateVariant(i, { stock: +e.target.value })} className="w-20 bg-transparent border border-[#E7DCC7] rounded px-2 py-1" /></td>
                                <td className="px-3 py-2"><input value={v.sku || ''} onChange={e => updateVariant(i, { sku: e.target.value })} className="w-40 bg-transparent border border-[#E7DCC7] rounded px-2 py-1 font-mono" /></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}

              {tab === 'personalization' && (
                <>
                  <label className="inline-flex items-center gap-2"><input type="checkbox" checked={!!editing.personalization_enabled} onChange={e => setEditing({ ...editing, personalization_enabled: e.target.checked })} /> <span className="text-sm">Enable personalization on this product</span></label>
                  {editing.personalization_enabled && (
                    <>
                      <label className="block"><span className="text-[11px] uppercase tracking-widest text-neutral-600">Field label</span><input value={editing.personalization_label || ''} onChange={e => setEditing({ ...editing, personalization_label: e.target.value })} placeholder="e.g. Engrave a name or verse" className="mt-1 w-full bg-[#F8F4EC] border border-[#E7DCC7] rounded-full px-4 py-2.5 text-sm outline-none focus:border-[#D4A63A]" /></label>
                      <label className="block"><span className="text-[11px] uppercase tracking-widest text-neutral-600">Max length</span><input type="number" value={editing.personalization_max_length || 40} onChange={e => setEditing({ ...editing, personalization_max_length: e.target.value })} className="mt-1 w-full bg-[#F8F4EC] border border-[#E7DCC7] rounded-full px-4 py-2.5 text-sm outline-none focus:border-[#D4A63A]" /></label>
                    </>
                  )}
                </>
              )}

              {tab === 'seo' && (
                <>
                  <label className="block"><span className="text-[11px] uppercase tracking-widest text-neutral-600">SEO Title</span><input value={editing.seo_title || ''} onChange={e => setEditing({ ...editing, seo_title: e.target.value })} className="mt-1 w-full bg-[#F8F4EC] border border-[#E7DCC7] rounded-full px-4 py-2.5 text-sm outline-none focus:border-[#D4A63A]" /></label>
                  <label className="block"><span className="text-[11px] uppercase tracking-widest text-neutral-600">SEO Description</span><textarea rows="3" value={editing.seo_description || ''} onChange={e => setEditing({ ...editing, seo_description: e.target.value })} className="mt-1 w-full bg-[#F8F4EC] border border-[#E7DCC7] rounded-lg px-4 py-2.5 text-sm outline-none focus:border-[#D4A63A]" /></label>
                  <label className="block"><span className="text-[11px] uppercase tracking-widest text-neutral-600">SEO Keywords (comma separated)</span><input value={editing.seo_keywords || ''} onChange={e => setEditing({ ...editing, seo_keywords: e.target.value })} className="mt-1 w-full bg-[#F8F4EC] border border-[#E7DCC7] rounded-full px-4 py-2.5 text-sm outline-none focus:border-[#D4A63A]" /></label>
                </>
              )}
            </div>

            <div className="px-6 py-5 border-t border-[#E7DCC7] flex items-center gap-3 shrink-0 bg-white">
              <button onClick={onSave} className="flex-1 bg-[#213A60] hover:bg-[#D4A63A] text-white py-3 rounded-full text-xs uppercase tracking-widest transition-colors">Save Product</button>
              <button onClick={() => setEditing(null)} className="px-6 py-3 border border-[#E7DCC7] rounded-full text-xs uppercase tracking-widest">Cancel</button>
            </div>
          </aside>
        </>
      )}

      {bulkOpen && (
        <>
          <div onClick={() => setBulkOpen(false)} className="fixed inset-0 bg-black/60 z-50" />
          <div className="fixed inset-x-4 top-16 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-[640px] z-50 bg-white rounded-2xl shadow-2xl overflow-hidden" data-testid="bulk-upload-modal">
            <div className="px-6 py-5 border-b border-[#E7DCC7] flex items-center justify-between">
              <div>
                <h3 className="font-serif-display text-2xl">Bulk Product Upload</h3>
                <p className="text-xs text-neutral-500 mt-0.5">Upload a CSV (from Google Sheets) or Excel (.xlsx) to bulk create / update products.</p>
              </div>
              <button onClick={() => setBulkOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="px-6 py-6 space-y-5">
              <div className="bg-[#F8F4EC] border border-[#E7DCC7] rounded-xl p-4 flex items-start gap-3">
                <Info className="w-4 h-4 text-[#8A6A22] mt-0.5 shrink-0" />
                <div className="text-xs text-neutral-700 leading-relaxed">
                  <strong>Step 1:</strong> Download the template below. It includes column headers with sample rows.<br />
                  <strong>Step 2:</strong> Fill your product data. Remove the sample rows and any lines starting with <code>#</code>.<br />
                  <strong>Step 3:</strong> Export as CSV or .xlsx, then upload here.<br />
                  <strong>Images:</strong> Use <code>image_1</code>–<code>image_4</code> columns. Each accepts a public URL or a <strong>Google Drive share link</strong>. Drive links are auto-fetched, resized to 1600px, converted to WebP, and served from our CDN. Set Drive sharing to <em>"Anyone with the link — Viewer"</em>. At least one image is required.<br />
                  Rows matching an existing <strong>SKU</strong> or <strong>slug</strong> will be updated; others will be created.
                </div>
              </div>

              <a
                href={productTemplateUrl()}
                download
                data-testid="download-template-btn"
                className="inline-flex items-center gap-2 bg-white border border-[#E7DCC7] hover:border-[#D4A63A] text-[#213A60] px-5 py-3 rounded-full text-xs uppercase tracking-widest transition-colors"
              >
                <Download className="w-4 h-4" /> Download CSV Template
              </a>

              <div className="border-2 border-dashed border-[#E7DCC7] rounded-2xl p-8 text-center bg-[#F8F4EC]/40">
                <FileSpreadsheet className="w-8 h-8 mx-auto text-[#8A6A22] mb-3" />
                <label className="inline-flex items-center gap-2 cursor-pointer bg-[#213A60] hover:bg-[#D4A63A] text-white px-5 py-2.5 rounded-full text-xs uppercase tracking-widest transition-colors">
                  <Upload className="w-4 h-4" /> {uploading ? 'Uploading…' : 'Choose CSV / Excel file'}
                  <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" data-testid="bulk-upload-input" disabled={uploading} onChange={e => doBulk(e.target.files?.[0])} className="hidden" />
                </label>
                <div className="text-[11px] text-neutral-500 mt-3">Accepts .csv, .xlsx, .xls · max ~2 MB recommended</div>
              </div>

              <div className="relative flex items-center gap-3 py-1">
                <span className="flex-1 border-t border-[#E7DCC7]" />
                <span className="text-[10px] uppercase tracking-widest text-neutral-500">Or import directly from Google Sheets</span>
                <span className="flex-1 border-t border-[#E7DCC7]" />
              </div>

              <div className="bg-white border border-[#E7DCC7] rounded-2xl p-5 space-y-3">
                <div className="text-xs text-neutral-600 leading-relaxed">
                  In Google Sheets: <strong>Share → Anyone with the link → Viewer</strong>. Paste the sheet URL below and we'll pull the rows live.
                </div>
                <div className="flex gap-2">
                  <input
                    value={sheetUrl}
                    onChange={e => setSheetUrl(e.target.value)}
                    placeholder="https://docs.google.com/spreadsheets/d/..."
                    data-testid="sheet-url-input"
                    className="flex-1 bg-[#F8F4EC] border border-[#E7DCC7] rounded-full px-4 py-2.5 text-sm outline-none focus:border-[#D4A63A]"
                  />
                  <button
                    onClick={doSheetImport}
                    disabled={uploading || !sheetUrl.trim()}
                    data-testid="sheet-import-btn"
                    className="inline-flex items-center gap-2 bg-[#213A60] hover:bg-[#D4A63A] disabled:bg-neutral-300 text-white px-5 py-2.5 rounded-full text-xs uppercase tracking-widest transition-colors whitespace-nowrap"
                  >
                    {uploading ? 'Importing…' : 'Import Sheet'}
                  </button>
                </div>
              </div>

              {bulkResult && (
                <div className="rounded-xl border border-[#E7DCC7] overflow-hidden" data-testid="bulk-upload-result">
                  <div className="px-4 py-3 bg-emerald-50 text-emerald-800 text-sm flex items-center justify-between">
                    <div><strong>{bulkResult.created}</strong> created · <strong>{bulkResult.updated}</strong> updated · <strong>{bulkResult.errors?.length || 0}</strong> errors · Total rows: {bulkResult.total_rows}</div>
                  </div>
                  {bulkResult.errors?.length > 0 && (
                    <div className="max-h-52 overflow-y-auto divide-y divide-[#E7DCC7] text-xs">
                      {bulkResult.errors.map((e, i) => (
                        <div key={i} className="px-4 py-2 flex gap-3 text-rose-700 bg-rose-50/40">
                          <span className="font-mono text-[10px] w-12 shrink-0">row {e.row}</span>
                          <span>{e.error}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminProducts;
